import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useAuth, tenantIdsWithRole } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, ShieldAlert, Search, X, ChevronDown,
  Building2, Workflow, Plug, CreditCard, Activity,
  ShieldCheck, Megaphone, FileCheck2, BookOpen, Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ExperienceModeGate } from "@/components/experience-mode-gate";

export const Route = createFileRoute("/_authenticated/app/admin")({ component: AdminLayout });

type AdminItem = { to: string; label: string; superOnly?: boolean; search?: Record<string, string> };
type AdminGroup = { id: string; label: string; icon: LucideIcon; items: AdminItem[] };

const GROUPS: AdminGroup[] = [
  {
    // Sprint 31 — Policy Library is the default Admin landing.
    // Every capability-specific "Rules" page is now a filtered lens
    // here; old URLs continue to work as lens shims.
    id: "policies",
    label: "Policy Library",
    icon: BookOpen,
    items: [
      { to: "/app/policies", label: "All Policies" },
      { to: "/app/policies", label: "Hold Creation",   search: { capability: "hold_creation" } },
      { to: "/app/policies", label: "Hold Release",    search: { capability: "hold_release" } },
      { to: "/app/policies", label: "Wave",            search: { capability: "wave" } },
      { to: "/app/policies", label: "Replenishment",   search: { capability: "replenishment" } },
      { to: "/app/policies", label: "Routing",         search: { capability: "routing" } },
      { to: "/app/policies", label: "Automation",      search: { capability: "automation_rules" } },
      { to: "/app/policies", label: "Delivery",        search: { capability: "delivery_commitment" } },
      { to: "/app/policies", label: "Planning",        search: { capability: "inventory_planning" } },
    ],
  },
  {
    id: "org",
    label: "Organization",
    icon: Building2,
    items: [
      { to: "/app/admin/tenants", label: "Tenants", superOnly: true },
      { to: "/app/admin/onboarding", label: "Onboarding" },
      { to: "/app/admin/blueprints", label: "Blueprints" },
      { to: "/app/admin/customers", label: "Customers" },
      { to: "/app/admin/facilities", label: "Facilities" },
      { to: "/app/admin/customer-facilities", label: "Customer Facilities" },
    ],
  },
  {
    id: "identity",
    label: "Identity & Security",
    icon: ShieldCheck,
    items: [
      { to: "/app/admin/users", label: "Users" },
      { to: "/app/admin/enterprise", label: "SSO & SCIM" },
      { to: "/app/admin/api-keys", label: "API Keys" },
    ],
  },
  {
    // Sprint 31 — Workflows + Schedules. NOT policies.
    // "Policies decide. Workflows orchestrate. Schedules schedule."
    id: "workflows",
    label: "Workflows",
    icon: Workflow,
    items: [
      { to: "/app/admin/workflows", label: "Order Workflows" },
      { to: "/app/admin/cycle-count-schedules", label: "Cycle Count Schedules" },
      { to: "/app/admin/fulfillment", label: "Fulfillment" },
    ],
  },
  {
    // Sprint 31 — Configurations (non-policy, non-workflow settings).
    id: "configurations",
    label: "Configurations",
    icon: SettingsIcon,
    items: [
      { to: "/app/admin/returns-config", label: "Returns" },
      { to: "/app/admin/slotting", label: "AI Slotting" },
      { to: "/app/admin/custom-fields", label: "Custom Fields" },
      { to: "/app/admin/inventory-adjustments", label: "Inventory Adjustments" },
      { to: "/app/admin/manifests", label: "Manifests & Pickups" },
      { to: "/app/admin/freight", label: "Freight & BOL" },
      { to: "/app/imports", label: "Imports" },
      { to: "/app/settings/data-ownership", label: "Data Ownership" },
    ],
  },
  {
    id: "integrations",
    label: "Integrations & Hardware",
    icon: Plug,
    items: [
      { to: "/app/admin/connections", label: "Connections" },
      { to: "/app/admin/exchange-runs", label: "Exchange Traces" },
      { to: "/app/admin/webhooks", label: "Webhooks" },
      { to: "/app/admin/edi", label: "EDI" },
      { to: "/app/admin/devices", label: "Devices" },
      { to: "/app/admin/hardware", label: "Automation Hardware" },
      { to: "/app/admin/print-queue", label: "Print Queue" },
      { to: "/app/admin/carriers", label: "Carriers" },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: CreditCard,
    items: [
      { to: "/app/admin/billing", label: "Billing & Usage" },
      { to: "/app/admin/cost-to-serve", label: "Cost to Serve" },
      { to: "/app/admin/consumables", label: "Consumables" },
    ],
  },
  {
    id: "comms",
    label: "Communications",
    icon: Megaphone,
    items: [
      { to: "/app/admin/notifications", label: "Notifications" },
      { to: "/app/admin/alerts", label: "Alerts" },
      { to: "/app/admin/templates", label: "Templates" },
      { to: "/app/admin/slack", label: "Slack" },
      { to: "/app/admin/google", label: "Google" },
      { to: "/app/admin/portal-reports", label: "Portal Reports" },
      { to: "/app/admin/portal-reports/access", label: "Portal Report Access" },
    ],
  },
  {
    id: "compliance",
    label: "Compliance & Audit",
    icon: FileCheck2,
    items: [
      { to: "/app/admin/compliance", label: "Compliance", superOnly: true },
      { to: "/app/admin/traceability", label: "Traceability" },
      { to: "/app/admin/audit", label: "Audit Log" },
      { to: "/app/admin/operational-signals", label: "Operational Signals" },
      { to: "/app/settings/compliance", label: "Compliance Vault" },
      { to: "/app/settings/retention", label: "Data Retention" },
    ],
  },
  {
    id: "reliability",
    label: "Reliability",
    icon: Activity,
    items: [
      { to: "/app/admin/status", label: "Status & SLOs" },
      { to: "/app/admin/observability", label: "Observability" },
      { to: "/app/admin/jobs", label: "Jobs" },
      { to: "/app/admin/oncall", label: "On-call" },
      { to: "/app/admin/reconciliation", label: "Reconciliation" },
      { to: "/app/settings/incidents", label: "Incident Console" },
      { to: "/app/settings/status-subscribers", label: "Status Subscribers" },
    ],
  },
];

function AdminLayout() {
  const { roles, loading } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const { isSuperAdmin: su } = useScope();
  const isAdminAnywhere = su || tenantIdsWithRole(roles, "tenant_admin").length > 0;
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const subRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!isAdminAnywhere) navigate({ to: "/app/operations/desk" });
  }, [loading, isAdminAnywhere, navigate]);

  // Sprint 31 — Admin lands on the Policy Library by default.
  // Hitting /app/admin with no sub-path drops the operator straight into
  // the Library, never a capability picker.
  useEffect(() => {
    if (loading || !isAdminAnywhere) return;
    if (loc.pathname === "/app/admin" || loc.pathname === "/app/admin/") {
      navigate({ to: "/app/policies", replace: true } as any);
    }
  }, [loading, isAdminAnywhere, loc.pathname, navigate]);

  // Visible groups (after super-only filter)
  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((i) => !i.superOnly || su),
      })).filter((g) => g.items.length > 0),
    [su],
  );

  // Active group/item derived from URL. The Policy Library group also
  // claims any /app/policies URL so the Library is the active section.
  const { activeGroup, activeItem } = useMemo(() => {
    if (loc.pathname.startsWith("/app/policies")) {
      const g = groups.find((g) => g.id === "policies");
      if (g) return { activeGroup: g, activeItem: g.items[0] };
    }
    for (const g of groups) {
      for (const i of g.items) {
        if (i.to !== "/app/policies" && loc.pathname.startsWith(i.to)) {
          return { activeGroup: g, activeItem: i };
        }
      }
    }
    return { activeGroup: groups[0], activeItem: groups[0]?.items[0] };
  }, [loc.pathname, groups]);

  // Search across all items (flat results)
  const searchResults = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const out: { group: string; item: AdminItem }[] = [];
    for (const g of groups) {
      for (const i of g.items) {
        if (i.label.toLowerCase().includes(term)) out.push({ group: g.label, item: i });
      }
    }
    return out.slice(0, 20);
  }, [q, groups]);

  // Auto-scroll active sub-item into view when group changes
  useEffect(() => {
    const el = subRowRef.current?.querySelector<HTMLAnchorElement>("[data-active='true']");
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeItem?.to]);

  // Render-gate: never mount admin children for non-admins, even for one frame.
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isAdminAnywhere) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-h-0 flex-col bg-background">
      {/* Settings header — two stacked horizontal rows, matches app feel */}
      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        {/* Row 1: title + category chips + search */}
        <div className="flex items-center gap-2 px-3 md:px-4 pt-2.5 pb-2 min-w-0">
          <h1 className="text-[14px] font-semibold tracking-tight shrink-0">Admin</h1>

          {/* Desktop: category chips — scroll horizontally if they overflow */}
          <nav className="hidden md:flex flex-1 min-w-0 items-center gap-0.5 ml-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {groups.map((g) => {
              const GIcon = g.icon;
              const isActive = activeGroup?.id === g.id;
              const first = g.items[0];
              return (
                <Link
                  key={g.id}
                  to={first.to}
                  title={g.label}
                  className={cn(
                    "shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 h-7 text-[12px] font-medium transition-colors",
                    isActive
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <GIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className={cn("whitespace-nowrap", isActive ? "inline" : "hidden")}>
                    {g.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Mobile: category dropdown */}
          <div className="md:hidden ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1.5 rounded-md border px-2.5 h-8 text-[13px]">
                  {activeGroup?.label}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {groups.map((g) => (
                  <DropdownMenuItem key={g.id} onSelect={() => navigate({ to: g.items[0].to })}>
                    <g.icon className="h-3.5 w-3.5 mr-2 opacity-70" /> {g.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="hidden md:block relative shrink-0 ml-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              placeholder="Search…"
              className="h-7 w-44 lg:w-56 pl-7 pr-7 text-[12.5px] bg-muted/50 border-transparent rounded-md shadow-none focus-visible:bg-background focus-visible:border-border focus-visible:ring-1 focus-visible:ring-ring/30"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {searchOpen && q && (
              <div className="absolute right-0 top-9 w-80 rounded-md border bg-popover shadow-lg overflow-hidden z-30">
                {searchResults.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-muted-foreground">No settings match “{q}”.</p>
                ) : (
                  <ul className="max-h-80 overflow-y-auto py-1">
                    {searchResults.map(({ group, item }) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={() => { setQ(""); setSearchOpen(false); }}
                          className="flex items-center justify-between px-3 py-2 text-[13px] hover:bg-muted"
                        >
                          <span className="truncate">{item.label}</span>
                          <span className="text-[11px] text-muted-foreground ml-3 shrink-0">{group}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: sub-items of active category */}
        <div ref={subRowRef} className="flex flex-wrap gap-x-1 gap-y-0 px-3 md:px-4">
          {activeGroup?.items.map((item) => {
            const active =
              item.to === "/app/policies"
                ? loc.pathname.startsWith("/app/policies")
                : loc.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                search={item.search as never}
                data-active={active}
                className={cn(
                  "shrink-0 whitespace-nowrap px-2.5 py-1.5 text-[12.5px] border-b-2 -mb-px transition-colors",
                  active
                    ? "border-primary text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        <ExperienceModeGate mode="admin">
          <Outlet />
        </ExperienceModeGate>
      </main>
    </div>
  );
}